import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CalendarDays, Clock, Phone } from 'lucide-react';
import { BLOG_POSTS } from '../content';

export default function BlogPreview() {
  const posts = BLOG_POSTS.slice(0, 4);
  const [main, ...rest] = posts;

  return (
    <section
      data-testid="blog-section"
      className="section"
      style={{
        background: 'var(--bg2)',
        borderTop: '1px solid var(--border)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Glow */}
      <div
        style={{
          position: 'absolute',
          top: '30%',
          left: '15%',
          width: 500,
          height: 500,
          background: 'radial-gradient(ellipse, rgba(229,51,51,.04) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <div className="wrap">
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            marginBottom: '3.5rem',
            gap: '2rem',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div className="pill" style={{ marginBottom: '1rem' }}>
              İçgörüler & Rehber
            </div>
            <h2 className="h2" style={{ marginBottom: '.875rem' }}>
              Lastik Bakım <span style={{ color: 'var(--blue)' }}>Kılavuzu</span>
            </h2>
            <p className="body" style={{ maxWidth: 480, color: 'var(--text2)' }}>
              Lastik bakımı, güvenli sürüş ve yol yardım ipuçları — uzmanlardan öneriler.
            </p>
          </div>
          <Link
            to="/blog"
            className="btn btn-ghost"
            style={{ display: 'inline-flex', gap: '.5rem' }}
          >
            Tüm Yazılar <ArrowRight size={15} />
          </Link>
        </div>

        {/* Conversion bridge CTA */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(229,51,51,.06) 0%, var(--card) 100%)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--rl)',
          padding: '1.25rem 1.75rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: '2.5rem', gap: '1rem', flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.625rem' }}>
            <span className="live" style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--green)', display: 'inline-block' }} />
            <span style={{ fontSize: '.85rem', color: 'var(--text2)' }}>
              Şu an yardıma mı ihtiyacınız var? <strong style={{ color: 'var(--text)' }}>Okumayı bırakın, arayın.</strong>
            </span>
          </div>
          <a href="tel:05349687611" className="btn btn-primary btn-sm num" style={{ gap: '.35rem', flexShrink: 0 }}>
            <Phone size={13} /> Hemen Ara
          </a>
        </div>

        {/* Editorial layout */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr',
            gap: '1.5rem',
          }}
          className="blog-grid"
        >
          {/* Featured */}
          <motion.div
            data-testid={`blog-card-${main.id}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: .6 }}
            style={{
              background: 'var(--card)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--rxl)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all .25s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--border2)';
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <Link
              to={`/blog/${main.slug}`}
              style={{
                display: 'block',
                overflow: 'hidden',
                height: 320,
                position: 'relative',
              }}
            >
              <img
                src={main.image}
                alt={main.title}
                loading="lazy"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform .6s ease',
                }}
                onMouseEnter={(e) => (e.target.style.transform = 'scale(1.05)')}
                onMouseLeave={(e) => (e.target.style.transform = 'scale(1)')}
              />
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(to top, rgba(5,5,13,.65) 0%, transparent 60%)',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  top: '1.25rem',
                  left: '1.25rem',
                  background: 'rgba(74,143,245,.15)',
                  border: '1px solid rgba(74,143,245,.35)',
                  backdropFilter: 'blur(12px)',
                  borderRadius: 100,
                  padding: '.35rem .875rem',
                  fontSize: '.72rem',
                  fontWeight: 700,
                  letterSpacing: '.06em',
                  textTransform: 'uppercase',
                  color: 'var(--blue)',
                }}
              >
                {main.category}
              </span>
            </Link>

            <div
              style={{
                padding: '2rem',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '.75rem',
                  fontSize: '.8rem',
                  color: 'var(--muted)',
                  marginBottom: '1rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '.35rem' }}>
                  <CalendarDays size={13} /> {main.date}
                </div>
                <span style={{ color: 'var(--border2)' }}>·</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '.35rem' }}>
                  <Clock size={13} /> 5 dk okuma
                </div>
              </div>

              <Link to={`/blog/${main.slug}`}>
                <h3
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    fontSize: '1.35rem',
                    color: 'var(--text)',
                    lineHeight: 1.3,
                    marginBottom: '.875rem',
                    transition: 'color .2s',
                  }}
                  onMouseEnter={(e) => (e.target.style.color = 'var(--blue)')}
                  onMouseLeave={(e) => (e.target.style.color = 'var(--text)')}
                >
                  {main.title}
                </h3>
              </Link>

              <p
                style={{
                  fontSize: '.9rem',
                  color: 'var(--text2)',
                  lineHeight: 1.7,
                  marginBottom: '1.5rem',
                }}
              >
                {main.excerpt}
              </p>

              <Link
                to={`/blog/${main.slug}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '.4rem',
                  color: 'var(--blue)',
                  fontSize: '.9rem',
                  fontWeight: 600,
                  marginTop: 'auto',
                  transition: 'gap .2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.gap = '.6rem')}
                onMouseLeave={(e) => (e.currentTarget.style.gap = '.4rem')}
              >
                Devamını Oku <ArrowRight size={15} />
              </Link>
            </div>
          </motion.div>

          {/* Small 3 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {rest.map((post, idx) => (
              <motion.div
                key={post.id}
                data-testid={`blog-card-${post.id}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: .5, delay: idx * .08 }}
                style={{
                  background: 'var(--card)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--rl)',
                  padding: '1.5rem',
                  display: 'flex',
                  gap: '1.25rem',
                  alignItems: 'flex-start',
                  flex: 1,
                  transition: 'all .2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--card2)';
                  e.currentTarget.style.borderColor = 'var(--border2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--card)';
                  e.currentTarget.style.borderColor = 'var(--border)';
                }}
              >
                <Link
                  to={`/blog/${post.slug}`}
                  style={{
                    width: 90,
                    height: 90,
                    borderRadius: 11,
                    overflow: 'hidden',
                    flexShrink: 0,
                    display: 'block',
                  }}
                >
                  <img
                    src={post.image}
                    alt={post.title}
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Link>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '.625rem',
                      marginBottom: '.5rem',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '.7rem',
                        fontWeight: 700,
                        letterSpacing: '.06em',
                        textTransform: 'uppercase',
                        color: 'var(--blue)',
                      }}
                    >
                      {post.category}
                    </span>
                    <span style={{ fontSize: '.72rem', color: 'var(--muted)' }}>{post.date}</span>
                  </div>

                  <Link to={`/blog/${post.slug}`}>
                    <h3
                      style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontWeight: 600,
                        fontSize: '.95rem',
                        color: 'var(--text)',
                        lineHeight: 1.4,
                        transition: 'color .2s',
                      }}
                      onMouseEnter={(e) => (e.target.style.color = 'var(--blue)')}
                      onMouseLeave={(e) => (e.target.style.color = 'var(--text)')}
                    >
                      {post.title}
                    </h3>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @media(max-width: 768px) {
          .blog-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
}
